import React from "react";
import { Session } from "../../../../Entities/Session";
import { Button, Divider, List, Modal, Typography } from "antd";
import {
  BlockOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  DisconnectOutlined,
  FullscreenExitOutlined,
  TableOutlined,
  ReloadOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

interface JobApplicationinterviewRoundsViolationModalProps {
  show: boolean;
  onClose: () => void;
  selectedSession: Session;
  roundName: string;
}
const JobApplicationinterviewRoundsViolationModal: React.FC<
  JobApplicationinterviewRoundsViolationModalProps
> = ({ show, onClose, selectedSession, roundName }) => {
  const { activityLogs, missedPings, lastPingAt } = selectedSession;

  const hasMissedPings = missedPings && missedPings > 0;
  const hasActivityLogs = activityLogs && activityLogs.length > 0;

  const violationMetadata: Record<
    string,
    { title: string; icon: React.ReactNode; description: string }
  > = {
    cursor_focus_switch: {
      title: "Cursor Focus Switch",
      icon: <CloseCircleOutlined />,
      description:
        "The candidate moved the mouse cursor to a different window or area during the interview.",
    },
    multiple_tabs: {
      title: "Multiple Tabs Detected",
      icon: <BlockOutlined />,
      description: "The candidate opened multiple tabs during the interview.",
    },
    keyboard_shortcut: {
      title: "Suspicious keyboard key usage",
      icon: <TableOutlined />,
      description:
        "The candidate used restricted keyboard shortcuts during the interview.",
    },
    missed_ping_limit_reached: {
      title: "Network connection issues",
      icon: <DisconnectOutlined />,
      description:
        "The candidate's network connection was interrupted or unstable during the interview.",
    },
    refresh_page: {
      title: "Page refresh attempt",
      icon: <ReloadOutlined />,
      description:
        "The candidate attempted to refresh the page during the interview.",
    },
    fullscreen_exit_attempt: {
      title: "Full screen exit attempt",
      icon: <FullscreenExitOutlined />,
      description:
        "The candidate attempted to exit the full screen mode during the interview.",
    },
    fullscreen_exit: {
      title: "Full screen exit",
      icon: <CloseCircleOutlined />,
      description:
        "The candidate exited the full screen mode during the interview.",
    },
    camera_covering: {
      title: "Camera Obstructed",
      icon: <VideoCameraOutlined />,
      description:
        "The candidate's camera was obstructed or covered during the interview.",
    },
  };

  const data: Array<{
    title: string;
    icon: React.ReactNode;
    description: React.ReactNode;
    timestamp: Date;
  }> = [];

  if (hasActivityLogs) {
    const activityLogsMap = activityLogs.reduce((acc, log) => {
      if (!acc[log.event]) {
        acc[log.event] = {
          count: 0,
          lastOccurred: null as Date | null,
          timestamps: [] as Date[],
        };
      }
      acc[log.event].count++;
      const logTimestamp = new Date(log.timestamp);
      acc[log.event].timestamps.push(logTimestamp);
      const currentLastOccurred = acc[log.event].lastOccurred;
      if (!currentLastOccurred || logTimestamp > currentLastOccurred) {
        acc[log.event].lastOccurred = logTimestamp;
      }
      return acc;
    }, {} as Record<string, { count: number; lastOccurred: Date | null; timestamps: Date[] }>);

    Object.entries(activityLogsMap).forEach(([event, violationInfo]) => {
      const metadata = violationMetadata[event];
      if (metadata && violationInfo.lastOccurred) {
        data.push({
          title: metadata.title,
          icon: metadata.icon,
          description: (
            <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
              {metadata.description}
              {violationInfo.count > 1 && (
                <>
                  {" "}
                  This violation occurred <strong>
                    {violationInfo.count}
                  </strong>{" "}
                  times.
                </>
              )}
              {violationInfo.lastOccurred && (
                <>
                  {" "}
                  Last occurred on{" "}
                  <strong>
                    {dayjs(violationInfo.lastOccurred).format(
                      "MMM D, YYYY h:mm A"
                    )}
                  </strong>
                  .
                </>
              )}
            </Typography.Paragraph>
          ),
          timestamp: violationInfo.lastOccurred,
        });
      }
    });
  }

  if (hasMissedPings && lastPingAt) {
    data.push({
      title: "Network connection issues",
      icon: <DisconnectOutlined />,
      description: (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          The candidate's network was unstable and got interrupted &nbsp;
          <strong>{missedPings}</strong>&nbsp; times during the interview. The
          last interruption was on &nbsp;
          <strong>{dayjs(lastPingAt).format("MMM D, YYYY h:mm A")}</strong>.
        </Typography.Paragraph>
      ),
      timestamp: new Date(lastPingAt),
    });
  }

  // Sort by timestamp (latest first)
  data.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  return (
    <Modal
      open={show}
      onCancel={onClose}
      destroyOnClose={true}
      title={null}
      footer={null}
      centered
      closeIcon={null}
    >
      <div className="job-application-interview-rounds-modal-container">
        <Typography.Title level={5} style={{ margin: 0 }}>
          Violation Report - {roundName}
        </Typography.Title>
        <Divider style={{ margin: 0 }} />

        <div>
          <List
            itemLayout="horizontal"
            dataSource={data}
            size="small"
            renderItem={(item, index) => (
              <List.Item key={index}>
                <List.Item.Meta
                  avatar={item.icon}
                  title={item.title}
                  description={item.description}
                />
              </List.Item>
            )}
          />
        </div>
        <div className="job-application-interview-rounds-modal-footer">
          <Button icon={<CloseOutlined />} type="primary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default JobApplicationinterviewRoundsViolationModal;
